import { useEffect } from "react";
import type Lenis from "lenis";

/**
 * Smooth wheel scrolling on desktop, the same setup as mundus-cras.
 *
 * Lenis is imported on demand, after the page has rendered, so it never
 * competes with the first paint. It is skipped entirely on touch screens
 * (native momentum scrolling is already smooth, and replacing it costs frames
 * on mid-range phones) and for visitors who ask for reduced motion.
 *
 * Nothing else may set `scroll-behavior: smooth` on <html>: with both active,
 * every programmatic scroll is animated twice and the two fight.
 */
let instance: Lenis | null = null;

/** Jump to the top of the page without animation, e.g. on route change. */
export function resetScroll() {
  if (instance) {
    instance.resize();
    instance.scrollTo(0, { immediate: true, force: true });
    return;
  }
  window.scrollTo(0, 0);
}

/** Animated return to the top, for "back to top" buttons. */
export function scrollToTop() {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    resetScroll();
    return;
  }
  if (instance) {
    instance.scrollTo(0, { duration: Math.min(1.4, Math.max(0.6, window.scrollY / 4200)) });
    return;
  }
  window.scrollTo({ top: 0, behavior: "smooth" });
}

/** Freeze page scrolling while a full-screen overlay (the mobile menu) is open. */
export function setScrollLocked(locked: boolean) {
  if (locked) instance?.stop();
  else instance?.start();
  document.documentElement.style.overflow = locked ? "hidden" : "";
}

const SmoothScroll = () => {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (window.matchMedia("(pointer: coarse)").matches) return;

    let lenis: Lenis | null = null;
    let frame = 0;
    let cancelled = false;

    // A history navigation restores a scroll position natively; stopping and
    // restarting drops any wheel travel still in flight so it doesn't paint
    // over the restored offset.
    const onPopState = () => {
      lenis?.stop();
      lenis?.start();
    };

    import("lenis").then(({ default: LenisCtor }) => {
      if (cancelled) return;
      lenis = new LenisCtor({
        lerp: 0.1,
        smoothWheel: true,
        wheelMultiplier: 1,
        anchors: true,
      });
      instance = lenis;
      window.addEventListener("popstate", onPopState);

      const raf = (time: number) => {
        lenis?.raf(time);
        frame = requestAnimationFrame(raf);
      };
      frame = requestAnimationFrame(raf);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      window.removeEventListener("popstate", onPopState);
      lenis?.destroy();
      if (instance === lenis) instance = null;
    };
  }, []);

  return null;
};

export default SmoothScroll;
